import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import apiClient from '@/services/api-client';
import { ApiResponse, User, UserState } from '@/services/types';
import { API_ENDPOINTS } from '@/utils/constants/end-points';

// ✅ Async Thunks
export const fetchUsers = createAsyncThunk<User[]>(
  'users/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiClient.get<ApiResponse<User[]>>(API_ENDPOINTS.USERS);
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const createUser = createAsyncThunk<User, Partial<User>>(
  'users/create',
  async (userData, { rejectWithValue }) => {
    try {
      const res = await apiClient.post<ApiResponse<User>>(
        API_ENDPOINTS.USERS,
        userData
      );
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const updateUser = createAsyncThunk<User, User>(
  'users/update',
  async (user, { rejectWithValue }) => {
    try {
      const res = await apiClient.put<ApiResponse<User>>(
        `${API_ENDPOINTS.USERS}/${user.id}`,
        user
      );
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const deleteUser = createAsyncThunk<string, string>(
  'users/delete',
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`${API_ENDPOINTS.USERS}/${id}`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ✅ Slice
const initialState: UserState = {
  data: [],
  loading: false,
  error: null,
};

const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {},
  extraReducers: builder => {
    // GET
    builder.addCase(fetchUsers.pending, state => {
      state.loading = true;
    });
    builder.addCase(fetchUsers.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload;
    });
    builder.addCase(fetchUsers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // POST
    builder.addCase(createUser.fulfilled, (state, action) => {
      state.data.push(action.payload);
    });

    // PUT
    builder.addCase(updateUser.fulfilled, (state, action) => {
      const index = state.data.findIndex(u => u.id === action.payload.id);
      if (index !== -1) state.data[index] = action.payload;
    });

    // DELETE
    builder.addCase(deleteUser.fulfilled, (state, action) => {
      state.data = state.data.filter(u => u.id !== action.payload);
    });
  },
});

export default userSlice.reducer;
